import { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from "recharts";
import { Users, CheckCircle, TrendingUp, Target, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";

const COACH_COLORS = ["#4A7AB5", "#F5A623", "#5BA88E", "#E06C75", "#C678DD", "#61DAFB", "#98C379", "#D4A574", "#7C8DB0"];
const DIM_LABELS = {
    ego_learning: "Ego vs Learning",
    authority_feedback: "Authority & Feedback",
    ownership: "Ownership",
    cognitive_flexibility: "Cognitive Flex",
    behavioral_followthrough: "Follow-through",
    reflection_depth: "Reflection"
};

function ScoreBar({ value, max = 100, color = "#4A7AB5" }) {
    const pct = Math.min((value / max) * 100, 100);
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs font-medium w-10 text-right tabular-nums" style={{ color }}>{value}</span>
        </div>
    );
}

function RankBadge({ rank }) {
    const colors = { 1: "bg-amber-100 text-amber-800", 2: "bg-slate-100 text-slate-700", 3: "bg-orange-100 text-orange-700" };
    return (
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${colors[rank] || "bg-muted text-muted-foreground"}`}>
            {rank}
        </span>
    );
}

export default function CoachComparison() {
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState("avg_score");
    const [sortDir, setSortDir] = useState("desc");
    const [selectedCoaches, setSelectedCoaches] = useState("all");

    useEffect(() => {
        api.get("/admin/coach-comparison")
            .then(res => setCoaches(res.data.coaches || []))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSort = (field) => {
        if (sortField === field) setSortDir(d => d === "desc" ? "asc" : "desc");
        else { setSortField(field); setSortDir("desc"); }
    };

    const filteredCoaches = useMemo(() => {
        let list = [...coaches];
        if (selectedCoaches === "with_data") list = list.filter(c => c.completed > 0);
        list.sort((a, b) => {
            const av = a[sortField] ?? 0, bv = b[sortField] ?? 0;
            return sortDir === "desc" ? bv - av : av - bv;
        });
        return list;
    }, [coaches, sortField, sortDir, selectedCoaches]);

    const coachesWithData = useMemo(() => coaches.filter(c => c.completed > 0), [coaches]);

    // Radar data for dimension comparison
    const radarData = useMemo(() => {
        const dims = Object.keys(DIM_LABELS);
        return dims.map(dk => {
            const row = { dimension: DIM_LABELS[dk] || dk };
            coachesWithData.forEach(c => {
                row[c.name] = c.dimension_averages?.[dk] || 0;
            });
            return row;
        });
    }, [coachesWithData]);

    // Bar chart data for key metrics
    const metricsBarData = useMemo(() => {
        return coachesWithData.map(c => ({
            name: c.name.length > 14 ? c.name.substring(0, 14) + "..." : c.name,
            fullName: c.name,
            "Avg Score": c.avg_score,
            "Avg APS": c.avg_aps,
            "Completion %": c.completion_rate,
        }));
    }, [coachesWithData]);

    // Totals
    const totals = useMemo(() => {
        const totalUsers = coaches.reduce((s, c) => s + c.total_users, 0);
        const totalCompleted = coaches.reduce((s, c) => s + c.completed, 0);
        const withData = coachesWithData;
        const avgScore = withData.length ? (withData.reduce((s, c) => s + c.avg_score, 0) / withData.length).toFixed(1) : 0;
        const avgAps = withData.length ? (withData.reduce((s, c) => s + c.avg_aps, 0) / withData.length).toFixed(1) : 0;
        return { totalUsers, totalCompleted, avgScore, avgAps, coachCount: coaches.length };
    }, [coaches, coachesWithData]);

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-muted-foreground/50" />;
        return sortDir === "desc" ? <ChevronDown className="h-3 w-3 text-foreground" /> : <ChevronUp className="h-3 w-3 text-foreground" />;
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    return (
        <div data-testid="coach-comparison-page" className="p-4 md:p-8 max-w-[1400px] mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit", color: "#1E3A5F" }}>
                        Coach Comparison
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">Side-by-side performance analysis across all coaches</p>
                </div>
                <Select value={selectedCoaches} onValueChange={setSelectedCoaches}>
                    <SelectTrigger data-testid="coach-filter" className="w-[180px]">
                        <SelectValue placeholder="Filter coaches" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Coaches ({coaches.length})</SelectItem>
                        <SelectItem value="with_data">With Data ({coachesWithData.length})</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* KPI Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                    { label: "Total Coaches", value: totals.coachCount, icon: Users, color: "#4A7AB5" },
                    { label: "Total Users", value: totals.totalUsers, icon: Users, color: "#F5A623" },
                    { label: "Completed", value: totals.totalCompleted, icon: CheckCircle, color: "#5BA88E" },
                    { label: "Avg Score", value: totals.avgScore, icon: TrendingUp, color: "#8CAED6" },
                    { label: "Avg APS", value: totals.avgAps, icon: Target, color: "#C678DD" },
                ].map(({ label, value, icon: Icon, color }) => (
                    <Card key={label} className="border shadow-sm">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="rounded-lg p-2" style={{ backgroundColor: `${color}15` }}>
                                <Icon className="h-4 w-4" style={{ color }} />
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">{label}</p>
                                <p className="text-lg font-bold" style={{ fontFamily: "Outfit", color }}>{value}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Charts Row */}
            {coachesWithData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Key Metrics Bar Chart */}
                    <Card className="border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit", color: "#1E3A5F" }}>
                                Performance Metrics
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[280px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={metricsBarData} margin={{ top: 10, right: 10, bottom: 30, left: 0 }}>
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} angle={-25} textAnchor="end" height={50} />
                                        <YAxis tick={{ fontSize: 11, fill: "#64748B" }} domain={[0, 100]} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12, fontFamily: "Outfit" }}
                                            formatter={(val, name) => [`${val}`, name]}
                                            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Outfit" }} />
                                        <Bar dataKey="Avg Score" fill="#4A7AB5" radius={[3, 3, 0, 0]} barSize={18} />
                                        <Bar dataKey="Avg APS" fill="#5BA88E" radius={[3, 3, 0, 0]} barSize={18} />
                                        <Bar dataKey="Completion %" fill="#F5A623" radius={[3, 3, 0, 0]} barSize={18} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Dimension Radar Overlay */}
                    <Card className="border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit", color: "#1E3A5F" }}>
                                Dimension Comparison
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[280px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                                        <PolarGrid stroke="#E2E8F0" />
                                        <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 10, fill: "#64748B", fontFamily: "Outfit" }} />
                                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: "#94A3B8" }} />
                                        {coachesWithData.map((c, i) => (
                                            <Radar
                                                key={c.id}
                                                name={c.name}
                                                dataKey={c.name}
                                                stroke={COACH_COLORS[i % COACH_COLORS.length]}
                                                fill={COACH_COLORS[i % COACH_COLORS.length]}
                                                fillOpacity={0.08}
                                                strokeWidth={2}
                                            />
                                        ))}
                                        <Legend wrapperStyle={{ fontSize: 11, fontFamily: "Outfit" }} />
                                        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12, fontFamily: "Outfit" }} />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Detailed Coach Table */}
            <Card className="border shadow-sm">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit", color: "#1E3A5F" }}>
                        Coach Rankings
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/30">
                                    <TableHead className="w-10 text-center">#</TableHead>
                                    <TableHead className="min-w-[140px]">Coach</TableHead>
                                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort("total_users")}>
                                        <span className="inline-flex items-center gap-1">Users <SortIcon field="total_users" /></span>
                                    </TableHead>
                                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort("completion_rate")}>
                                        <span className="inline-flex items-center gap-1">Rate <SortIcon field="completion_rate" /></span>
                                    </TableHead>
                                    <TableHead className="min-w-[120px] cursor-pointer select-none" onClick={() => toggleSort("avg_score")}>
                                        <span className="inline-flex items-center gap-1">Avg Score <SortIcon field="avg_score" /></span>
                                    </TableHead>
                                    <TableHead className="min-w-[100px] cursor-pointer select-none" onClick={() => toggleSort("avg_aps")}>
                                        <span className="inline-flex items-center gap-1">Avg APS <SortIcon field="avg_aps" /></span>
                                    </TableHead>
                                    <TableHead className="min-w-[100px] cursor-pointer select-none" onClick={() => toggleSort("avg_defense")}>
                                        <span className="inline-flex items-center gap-1">Avg Defense <SortIcon field="avg_defense" /></span>
                                    </TableHead>
                                    <TableHead className="min-w-[140px]">Top Archetype</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredCoaches.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                                            No coaches found
                                        </TableCell>
                                    </TableRow>
                                ) : filteredCoaches.map((c, idx) => {
                                    const topArch = Object.entries(c.archetype_distribution || {}).sort((a, b) => b[1] - a[1])[0];
                                    const color = COACH_COLORS[idx % COACH_COLORS.length];
                                    return (
                                        <TableRow key={c.id} data-testid={`coach-row-${c.id}`} className="hover:bg-muted/20 transition-colors">
                                            <TableCell className="text-center"><RankBadge rank={idx + 1} /></TableCell>
                                            <TableCell>
                                                <div>
                                                    <p className="font-semibold text-sm" style={{ fontFamily: "Outfit" }}>{c.name}</p>
                                                    <p className="text-xs text-muted-foreground">{c.organization || c.email}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <span className="font-semibold text-sm">{c.total_users}</span>
                                                <span className="text-xs text-muted-foreground ml-1">({c.completed}/{c.pending})</span>
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <Badge variant={c.completion_rate >= 50 ? "default" : "secondary"}
                                                    className="text-xs"
                                                    style={c.completion_rate >= 50 ? { backgroundColor: "#5BA88E", color: "#fff" } : {}}>
                                                    {c.completion_rate}%
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {c.completed > 0 ? <ScoreBar value={c.avg_score} color={color} /> : <span className="text-xs text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell>
                                                {c.completed > 0 ? <ScoreBar value={c.avg_aps} color="#5BA88E" /> : <span className="text-xs text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell>
                                                {c.completed > 0 ? (
                                                    <span className="text-sm font-medium tabular-nums" style={{ color: c.avg_defense <= 3 ? "#5BA88E" : c.avg_defense <= 6 ? "#F5A623" : "#E06C75" }}>
                                                        {c.avg_defense}
                                                    </span>
                                                ) : <span className="text-xs text-muted-foreground">—</span>}
                                            </TableCell>
                                            <TableCell>
                                                {topArch ? (
                                                    <Badge variant="outline" className="text-xs font-medium">
                                                        {topArch[0].length > 20 ? topArch[0].substring(0, 20) + "..." : topArch[0]}
                                                    </Badge>
                                                ) : <span className="text-xs text-muted-foreground">—</span>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Dimension Breakdown Table */}
            {coachesWithData.length > 0 && (
                <Card className="border shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold" style={{ fontFamily: "Outfit", color: "#1E3A5F" }}>
                            Dimension Breakdown by Coach
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/30">
                                        <TableHead className="min-w-[130px]">Dimension</TableHead>
                                        {coachesWithData.map((c, i) => (
                                            <TableHead key={c.id} className="text-center min-w-[100px]">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COACH_COLORS[i % COACH_COLORS.length] }} />
                                                    <span className="text-xs">{c.name.split(" ")[0]}</span>
                                                </span>
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {Object.entries(DIM_LABELS).map(([dk, label]) => {
                                        const vals = coachesWithData.map(c => c.dimension_averages?.[dk] || 0);
                                        const maxVal = Math.max(...vals);
                                        return (
                                            <TableRow key={dk}>
                                                <TableCell className="font-medium text-sm">{label}</TableCell>
                                                {coachesWithData.map((c, i) => {
                                                    const v = c.dimension_averages?.[dk] || 0;
                                                    const isMax = v === maxVal && v > 0;
                                                    return (
                                                        <TableCell key={c.id} className="text-center">
                                                            <span className={`text-sm tabular-nums ${isMax ? "font-bold" : ""}`}
                                                                style={{ color: isMax ? COACH_COLORS[i % COACH_COLORS.length] : undefined }}>
                                                                {v > 0 ? `${v}%` : "—"}
                                                            </span>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
